import React, {
  createElement,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';

import {
  getDocumentName,
  isDocumentImage,
  isDocumentReadOnly,
  getDocumentFileType,
  getDocumentCreatedDate,
  getDocumentOwnedByName,
  getDocumentCompanyName,
  getOriginalDocumentPath,
  getFileThumbNailForDocumentView,
  getDocumentIsBuilding,
  isDocumentsFolder,
} from '../../../../utils/documents';
import styles from './styles';
import Image from '../../../Image';
import Box from '../../../atomic/Box';
import ListItem from '../../../atomic/ListItem';
import Checkbox from '../../../atomic/Checkbox';
import TextHighlighted from '../../../TextHighlighted';
import ListItemText from '../../../atomic/ListItemText';
import { formattedDate } from '../../../../utils/dates';
import ListItemSecondaryAction from '../../../atomic/ListItemSecondaryAction';
import FileImageIcon from '../../../../assets/icons/DocumentViewIcons/FileImageIcon';
import Tooltip from '../../../Tooltip';
import LockForDocView from '../../../../assets/icons/LockForDocView';
import BuildingDocument from '../../../../assets/icons/BuildingDocument';
import FolderClosed from '../../../../assets/icons/FolderClosed';
import ListItemButton from './ListItemButton';
import FolderSystemGenerated from '../../../../assets/icons/FolderSystemGenerated';

const DocumentListItem = ({
  classes,
  document,
  onFolderChange,
  newUpload,
  onMenuClick,
  searchQuery,
  actionsEnabled,
  selectedDocuments = [],
  onSelect = () => {},
  reset = false,
  isPersonalDocuments = false,
  isFromPersonal,
  headerHeight,
  hoveredListItem,
  handleDragOver,
  publicSection = false,
}) => {
  const [options, setOptions] = useState(selectedDocuments);
  const [checkDocumentItem, setCheckDocumentItem] = useState(null);

  const listItemRef = useRef(null);

  useEffect(() => {
    const { current } = listItemRef;
    if (current) {
      current.dropDocument = document; // Store docData on the element
    }
  }, [document]);

  const isFolder = isDocumentsFolder(document);

  const onMenuButtonClick = (focusedDocument, event) => {
    event.preventDefault();
    onMenuClick(focusedDocument, event);
  };

  const docTitle = getDocumentName(document);
  const ownedByName = getDocumentOwnedByName(document);
  const companyName = getDocumentCompanyName(document);
  const uploadedByName = ownedByName || companyName;
  const createdDate = formattedDate(getDocumentCreatedDate(document));

  let secondaryText;

  if (isPersonalDocuments === true || isFolder) {
    secondaryText = createdDate;
  } else {
    secondaryText = [createdDate, uploadedByName].join(' • ');
  }

  useEffect(() => {
    onSelect(options);
  }, [options.length]);

  useEffect(() => {
    if (reset) {
      setOptions([]);
    }
  }, [reset]);

  const handleChange = (o) => {
    if (checkDocumentItem) {
      setCheckDocumentItem(null);
      onSelect(o);
    } else {
      setCheckDocumentItem(o);
      onSelect(o);
    }
  };

  const renderedIcon = useMemo(() => {
    if (isDocumentImage(document)) {
      return (
        <>
          <Image
            fallbackComponent={<FileImageIcon className={classes.imageIcon} />}
            alt={docTitle}
            src={getOriginalDocumentPath(document)}
            className={classes.thumbnailImageIcon}
          />
        </>
      );
    }
    if (isFolder) {
      return (
        <>
          {document?.isSystemGenerated && (
            <FolderSystemGenerated className={classes.systemFolderIcon} />
          )}
          <FolderClosed />
        </>
      );
    }
    return createElement(
      getFileThumbNailForDocumentView(getDocumentFileType(document)) ||
        getFileThumbNailForDocumentView('default'),
      { className: classes.imageIcon }
    );
  }, [document]);

  return (
    <ListItem
      className={clsx(
        classes.listItem,
        'mobile-list-item',
        'word-break',
        !actionsEnabled && classes.actionsDisabled
      )}
      style={{
        backgroundColor: hoveredListItem === listItemRef.current && '#EEEEEE',
      }}
      data-drop-target="true"
      ref={listItemRef}
    >
      {actionsEnabled && (
        <ListItemSecondaryAction className={classes.listItemSecondaryAction}>
          <Checkbox
            onChange={() => handleChange(document)}
            checked={
              !!selectedDocuments.find((value) => value.id === document?.id)
            }
            className={clsx([
              classes.checkbox,
              isFolder && classes.folderCheckbox,
            ])}
            disabled={
              isDocumentReadOnly(document) || getDocumentIsBuilding(document)
            }
          />
        </ListItemSecondaryAction>
      )}
      <ListItemButton
        classes={classes}
        documentObject={document}
        isFolder={isFolder}
        isFromPersonal={isFromPersonal}
        isPersonalDocuments={isPersonalDocuments}
        onFolderChange={onFolderChange}
        newUpload={newUpload}
        docTitle={docTitle}
        renderedIcon={renderedIcon}
        headerHeight={headerHeight}
        handleDragOver={handleDragOver}
        publicSection={publicSection}
      >
        <span className={classes.imageIconBox}>{renderedIcon}</span>
        <ListItemText
          primary={
            <div className={classes.primaryText}>
              {isDocumentReadOnly(document) && (
                <Tooltip
                  title="Filen inneholder historisk data, og kan ikke endres"
                  interactive
                  placement="bottom"
                  arrow
                  popperArrow
                >
                  <div className={classes.lockForDocViewContainer}>
                    <LockForDocView width={14} height={14} />
                  </div>
                </Tooltip>
              )}

              {getDocumentIsBuilding(document) && (
                <Tooltip
                  title="Filen er knyttet til bygningen og ikke boenheten og kan derfor ikke redigeresFilen er knyttet til bygningen og ikke boenheten, og den kan derfor kun endres av avsender/bedriften"
                  interactive
                  placement="bottom"
                  arrow
                  popperArrow
                >
                  <div className={classes.builidingDocViewContainer}>
                    <BuildingDocument width={12} height={12} />
                  </div>
                </Tooltip>
              )}
              <TextHighlighted searchQuery={searchQuery}>
                {docTitle}
              </TextHighlighted>
            </div>
          }
          secondaryTypographyProps={{ component: 'div' }}
          secondary={
            <Box>
              <small className={classes.secondaryText}>
                <TextHighlighted searchQuery={searchQuery}>
                  {secondaryText}
                </TextHighlighted>
              </small>
            </Box>
          }
        />
      </ListItemButton>
      <ListItemSecondaryAction className={classes.secondaryAction}>
        {actionsEnabled && (
          <MoreVert
            className={classes.menuIcon}
            onClick={(event) => onMenuButtonClick(document, event)}
          />
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export default withStyles(styles)(DocumentListItem);
